import React, { useEffect, useState, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import jsPDF from 'jspdf';
import './WeekInvoices.css';
import MonthInvoices from './MonthInvoices';
import { 
  FaCalendarWeek, 
  FaSearch, 
  FaTimes, 
  FaEuroSign, 
  FaShoppingCart, 
  FaFilePdf, 
  FaChevronDown, 
  FaChevronUp,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa';

const WeekInvoices = () => {
  const [weeklyData, setWeeklyData] = useState([]);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [searchDate, setSearchDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const calculateWeeklyData = useCallback((commandes) => {
    const weeks = {};

    commandes.forEach((commande) => {
      const orderDate = new Date(commande.date.toDate());
      const startOfWeek = getStartOfWeek(orderDate);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);

      const weekKey = `${formatDate(startOfWeek)} - ${formatDate(endOfWeek)}`;

      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          revenue: 0,
          dishes: 0,
          details: [],
        };
      }

      weeks[weekKey].revenue += commande.total;
      weeks[weekKey].dishes += commande.items.length;
      weeks[weekKey].details.push(commande);
    });

    // Convertir l'objet en tableau, trier les semaines par date (du plus récent au plus ancien)
    return Object.entries(weeks)
      .map(([key, value]) => ({
        week: key,
        startDate: new Date(key.split(' - ')[0].split('/').reverse().join('-')), // Obtenir la date de début pour le tri
        ...value,
      }))
      .sort((a, b) => b.startDate - a.startDate); // Tri par date de début, du plus récent au plus ancien
  }, []);

  useEffect(() => {
    const fetchWeeklyData = async () => {
      try {
        setLoading(true);
        setError(null);
        
      const commandesSnapshot = await getDocs(collection(db, 'total_commande'));
      const commandes = commandesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const weeks = calculateWeeklyData(commandes);
      setWeeklyData(weeks);
        setSuccessMessage('Données chargées avec succès');
        
        // Effacer le message de succès après 3 secondes
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        setError('Erreur lors du chargement des données');
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWeeklyData();
  }, [calculateWeeklyData]);

  // Gestion responsive du nombre d'éléments par page
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setItemsPerPage(3);
      } else {
        setItemsPerPage(5);
      }
    };

    // Appeler une fois au montage
    handleResize();

    // Écouter les changements de taille
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getStartOfWeek = (date) => {
    const day = date.getDay();
    const diff = day === 0 ? 6 : day - 1; // Dimanche = dernier jour
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - diff);
    startOfWeek.setHours(0, 0, 0, 0);
    return startOfWeek;
  };

  const formatDate = (date) => {
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${date.getFullYear()}`;
  };

  const formatOrderDate = (date) => {
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${date.getFullYear()}`;
  };

  const generatePDF = (week) => {
    try {
    const pdf = new jsPDF();
      
      // En-tête
      pdf.setFontSize(18);
      pdf.setFont(undefined, 'bold');
      pdf.text('SKANEAT - Rapport Hebdomadaire', 20, 20);
      
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Période : ${week.week}`, 20, 35);
      
      // Informations générales
    pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Résumé de la semaine', 20, 50);
      
      pdf.setFont(undefined, 'normal');
      pdf.text(`Revenus totaux : ${week.revenue.toFixed(2)} €`, 20, 65);
      pdf.text(`Nombre de commandes : ${week.details.length}`, 20, 75);
      pdf.text(`Nombre de plats vendus : ${week.dishes}`, 20, 85);
      
      // Détails des commandes
      pdf.setFont(undefined, 'bold');
      pdf.text('Détail des commandes', 20, 105);

    pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      
      let yPosition = 120;
    week.details
      .sort((a, b) => {
        const dateA = new Date(a.date.toDate());
        const dateB = new Date(b.date.toDate());
        return dateA - dateB;
      })
      .forEach((commande, index) => {
          if (yPosition > 280) {
            pdf.addPage();
            yPosition = 20;
          }
          
        const orderDate = new Date(commande.date.toDate());
        const formattedDate = formatOrderDate(orderDate);
          const time = orderDate.toLocaleTimeString('fr-FR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          });
          
          pdf.text(`${index + 1}. ${formattedDate} à ${time}`, 20, yPosition);
          pdf.text(`   Montant: ${commande.total.toFixed(2)} €`, 25, yPosition + 8);
          pdf.text(`   Plats: ${commande.items.length}`, 25, yPosition + 16);
          
          yPosition += 30;
        });
      
      // Pied de page
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.text(`Page ${i} sur ${pageCount}`, 20, pdf.internal.pageSize.height - 10);
        pdf.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, 
                 pdf.internal.pageSize.width - 60, pdf.internal.pageSize.height - 10);
      }
      
      pdf.save(`Rapport_Semaine_${week.week.replace(/\//g, '_').replace(/\s/g, '_')}.pdf`);
      setSuccessMessage('PDF généré avec succès');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Erreur lors de la génération du PDF');
      console.error('Erreur PDF:', err);
    }
  };

  // Fonction de filtrage des semaines par date
  const filterByDate = (weeks) => {
    if (!searchDate) return weeks; // Si aucune date n'est saisie, on retourne toutes les semaines

    // Convertir la date de recherche en objet Date
    const [day, month, year] = searchDate.split('/').map(Number);
    const searchDateObj = new Date(year, month - 1, day);

    // Filtrer les semaines qui contiennent cette date
    return weeks.filter((week) => {
      const startOfWeek = new Date(week.startDate);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);

      // Vérifier si la date recherchée est dans la semaine
      return searchDateObj >= startOfWeek && searchDateObj <= endOfWeek;
    });
  };

  // Fonctions de pagination
  const filteredWeeks = filterByDate(weeklyData);
  const totalPages = Math.ceil(filteredWeeks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentWeeks = filteredWeeks.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedWeek(null); // Fermer le modal si ouvert
  };

  const handleSearchChange = (e) => {
    setSearchDate(e.target.value);
    setCurrentPage(1); // Reset à la première page lors de la recherche
  };

  if (loading) {
    return (
      <div className="week-invoices-container">
        <div className="week-invoices-loading-container">
          <div className="week-invoices-loading-spinner"></div>
          <p className="week-invoices-loading-text">Chargement des factures hebdomadaires...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="week-invoices-container">
      {/* Header */}
      <div className="week-invoices-header">
        <div className="week-invoices-title-section">
          <FaCalendarWeek className="week-invoices-title-icon" />
          <h1 className="week-invoices-title">Factures Hebdomadaires</h1>
        </div>
        <p className="week-invoices-subtitle">
          Consultez et exportez les rapports de ventes par semaine
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="week-invoices-error-message">
          <FaExclamationTriangle className="week-invoices-error-icon" />
          <span>{error}</span>
        </div>
      )}
      
      {successMessage && (
        <div className="week-invoices-success-message">
          <FaCheckCircle className="week-invoices-success-icon" />
          <span>{successMessage}</span>
        </div>
      )}
      
      {/* Section de recherche */}
      <div className="week-invoices-search-section">
        <div className="week-invoices-search-container">
          <FaSearch className="week-invoices-search-icon" />
        <input
          type="text"
            className="week-invoices-search-input"
          value={searchDate}
            onChange={handleSearchChange}
          placeholder="Rechercher une semaine (ex: 01/12/2025)"
        />
      </div>
      </div>

      {/* Statistiques globales */}
      {weeklyData.length > 0 && (
        <div className="week-invoices-stats-grid">
          <div className="week-invoices-stat-card">
            <div className="week-invoices-stat-icon">
              <FaCalendarWeek />
            </div>
            <div className="week-invoices-stat-content">
              <div className="week-invoices-stat-value">{weeklyData.length}</div>
              <div className="week-invoices-stat-label">Semaines</div>
            </div>
          </div>
          
          <div className="week-invoices-stat-card">
            <div className="week-invoices-stat-icon">
              <FaEuroSign />
            </div>
            <div className="week-invoices-stat-content">
              <div className="week-invoices-stat-value">
                {weeklyData.reduce((sum, week) => sum + week.revenue, 0).toFixed(2)}€
              </div>
              <div className="week-invoices-stat-label">Revenus totaux</div>
            </div>
          </div>
          
          <div className="week-invoices-stat-card">
            <div className="week-invoices-stat-icon">
              <FaShoppingCart />
            </div>
            <div className="week-invoices-stat-content">
              <div className="week-invoices-stat-value">
                {weeklyData.reduce((sum, week) => sum + week.details.length, 0)}
              </div>
              <div className="week-invoices-stat-label">Commandes totales</div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des semaines */}
      <div className="week-invoices-weeks-section">
        <h2 className="week-invoices-section-title">
          <FaCalendarWeek className="week-invoices-section-icon" />
          Semaines disponibles
        </h2>
        
        {filteredWeeks.length === 0 ? (
          <div className="week-invoices-empty-state">
            <FaCalendarWeek className="week-invoices-empty-icon" />
            <p className="week-invoices-empty-text">
              {searchDate ? 'Aucune semaine trouvée pour cette date' : 'Aucune donnée disponible'}
            </p>
          </div>
        ) : (
          <>
            <div className="week-invoices-weeks-grid">
              {currentWeeks.map((week, index) => (
              <div key={index} className="week-invoices-week-card">
                <div className="week-invoices-week-header">
                  <div className="week-invoices-week-info">
                    <h3 className="week-invoices-week-title">Semaine {week.week}</h3>
                    <div className="week-invoices-week-stats">
                      <span className="week-invoices-week-revenue">
                        <FaEuroSign /> {week.revenue.toFixed(2)}€
                      </span>
                      <span className="week-invoices-week-orders">
                        <FaShoppingCart /> {week.details.length} commandes
                      </span>
                    </div>
                  </div>
            <button
                    className="week-invoices-expand-btn"
              onClick={() => setExpandedWeek(expandedWeek === index ? null : index)}
            >
                    {expandedWeek === index ? <FaChevronUp /> : <FaChevronDown />}
            </button>
                </div>
                
            {expandedWeek === index && (
              <>
                    <div className="week-invoices-modal-overlay" onClick={() => setExpandedWeek(null)} />
                    <div className="week-invoices-modal">
                      <div className="week-invoices-modal-header">
                        <h2 className="week-invoices-modal-title">
                          <FaCalendarWeek className="week-invoices-modal-icon" />
                          Détails de la semaine {week.week}
                        </h2>
                        <button 
                          className="week-invoices-close-btn"
                          onClick={() => setExpandedWeek(null)}
                        >
                          <FaTimes />
                        </button>
                      </div>
                      
                      <div className="week-invoices-modal-content">
                        <div className="week-invoices-modal-stats">
                          <div className="week-invoices-modal-stat">
                            <FaEuroSign className="week-invoices-modal-stat-icon" />
                            <div className="week-invoices-modal-stat-content">
                              <div className="week-invoices-modal-stat-value">{week.revenue.toFixed(2)}€</div>
                              <div className="week-invoices-modal-stat-label">Revenus totaux</div>
                            </div>
                          </div>
                          
                          <div className="week-invoices-modal-stat">
                            <FaShoppingCart className="week-invoices-modal-stat-icon" />
                            <div className="week-invoices-modal-stat-content">
                              <div className="week-invoices-modal-stat-value">{week.details.length}</div>
                              <div className="week-invoices-modal-stat-label">Commandes</div>
                            </div>
                          </div>
                          
                          <div className="week-invoices-modal-stat">
                            <FaShoppingCart className="week-invoices-modal-stat-icon" />
                            <div className="week-invoices-modal-stat-content">
                              <div className="week-invoices-modal-stat-value">{week.dishes}</div>
                              <div className="week-invoices-modal-stat-label">Plats vendus</div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="week-invoices-modal-orders">
                          <h3 className="week-invoices-modal-orders-title">Détail des commandes</h3>
                          <div className="week-invoices-modal-orders-list">
                    {week.details
                      .sort((a, b) => {
                        const dateA = new Date(a.date.toDate());
                        const dateB = new Date(b.date.toDate());
                        return dateA - dateB;
                      })
                      .map((commande, idx) => {
                        const orderDate = new Date(commande.date.toDate());
                        const formattedDate = formatOrderDate(orderDate);
                                const time = orderDate.toLocaleTimeString('fr-FR', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                });
                                
                        return (
                                  <div key={idx} className="week-invoices-order-item">
                                    <div className="week-invoices-order-date">
                                      {formattedDate} à {time}
                                    </div>
                                    <div className="week-invoices-order-details">
                                      <span className="week-invoices-order-amount">
                                        {commande.total.toFixed(2)}€
                                      </span>
                                      <span className="week-invoices-order-plates">
                                        {commande.items.length} plats
                                      </span>
                                    </div>
                                  </div>
                        );
                      })}
                          </div>
                        </div>
                        
                        <div className="week-invoices-modal-actions">
                          <button 
                            className="week-invoices-download-btn"
                            onClick={() => generatePDF(week)}
                          >
                            <FaFilePdf className="week-invoices-download-icon" />
                            Télécharger le PDF
                          </button>
                        </div>
                      </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="week-invoices-pagination">
                <div className="week-invoices-pagination-info">
                  Affichage de {startIndex + 1} à {Math.min(endIndex, filteredWeeks.length)} sur {filteredWeeks.length} semaines
                </div>
                <div className="week-invoices-pagination-controls">
                  <button
                    className="week-invoices-pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Précédent
                  </button>
                  
                  <div className="week-invoices-pagination-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        className={`week-invoices-pagination-number ${currentPage === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    className="week-invoices-pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Section des factures mensuelles */}
      <div className="week-invoices-monthly-section">
        <MonthInvoices />
      </div>
    </div>
  );
};

export default WeekInvoices;
